import SaveToWorkspace from '@patsnap-biz/save-to-workspace-v2'
import { query2Text, structureQuery2Text } from '@patsnap/synapse_common_business'
import { ISearchParams } from '@patsnap/synapse_common_interface'
import { useDrugFinderExpertSearch } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { VUE_APP_WORKSPACE_BASEURL, VUE_APP_WORKSPACE_DOMAIN_URL } from '@pharmsnap/pharmsnap-web/config'
import { getAxios } from '@pharmsnap/pharmsnap-web/service/service'
import { useLocale } from '@pharmsnap/shared/src/composition/useLocale'
import { sharedCtx } from '@pharmsnap/shared/src/context'
import { ComponentInstance } from '@vue/composition-api'
import { AxiosInstance } from 'axios'
import { assign, cloneDeep } from 'lodash'
import { Toast } from 'patsnap-biz'
import { IWorkspaceOriginParams, IWorkspaceServiceParams } from '../../type'
import { formatSubmitSaveToWorkspaceParams, getWorkspaceSearchId } from '../../util'

const { DrugService } = SaveToWorkspace

export default class SaveDrugService extends DrugService {
  vm: ComponentInstance | undefined = undefined
  axiosInstance: AxiosInstance
  errorCodeI18nPrefix: string
  selectedIds: string[] = []
  workspaceSearchId: string

  constructor(params: IWorkspaceServiceParams) {
    super()
    const { convertFromStandardQueryItemsToQueryString } = useDrugFinderExpertSearch()
    const { locale } = useLocale()

    const { vm, selectedIds = [], searchParams = {} as ISearchParams, total = 0 } = params
    this.vm = vm
    this.axiosInstance = getAxios()
    // 用于发送请求的前缀
    this.baseUrl = `${VUE_APP_WORKSPACE_BASEURL}/workspace/web`
    // 成功保存后用于生成跳转链接
    this.workspaceProductDomain = VUE_APP_WORKSPACE_DOMAIN_URL
    this.errorCodeI18nPrefix = 'errorMessage'
    this.selectedIds = selectedIds
    this.searchParams = searchParams
    this.workspaceSearchId = ''

    // 关键区别：根据是否有searchParams.query来判断是否需要自动更新功能
    // 药物详情页：没有searchParams.query，不需要自动更新
    // 药物列表页：有searchParams.query，需要自动更新
    const hasSearchParams = searchParams && searchParams.query

    if (hasSearchParams) {
      const {
        query: { must },
        query_type,
        structure_query,
      } = searchParams as ISearchParams
      let mustQuery2TextCn = ''
      let mustQuery2TextEn = ''
      let queryInfo: any = searchParams?.query || {}
      if (query_type === 'EXPERT') {
        const mustQuery = cloneDeep(must?.[0] || [])
        mustQuery2TextCn = convertFromStandardQueryItemsToQueryString(mustQuery, 'cn')
        mustQuery2TextEn = convertFromStandardQueryItemsToQueryString(mustQuery, 'en')
      } else if (query_type === 'STRUCTURE') {
        mustQuery2TextCn = structureQuery2Text(structure_query || {}, 'cn')
        mustQuery2TextEn = structureQuery2Text(structure_query || {}, 'en')
        queryInfo = structure_query
      } else {
        mustQuery2TextCn = query2Text(must || [], 'cn', 'drug')
        mustQuery2TextEn = query2Text(must || [], 'en', 'drug')
      }
      // filter 转换成query·字符串
      const filterString = query2Text(queryInfo.filter, 'cn', 'drug')

      this.enableAutoUpdate = true
      const q = locale.value === 'cn' ? mustQuery2TextCn : mustQuery2TextEn
      this.autoUpdate = {
        enable: false,
        efq: filterString,
        q,
        type: 'drug',
      }

      this.autoCollectConfig = {
        drug: {
          display_query: q,
          collect_on_off: false,
          collect_query: {
            query: { ...queryInfo },
            query_type,
            filter_query: filterString,
          },
        },
      }
    }
    const selectCount: number = selectedIds.length

    if (selectCount > 0) {
      // 范围选择组件参数
      this.rangeSelector = {
        ...this.rangeSelector,
        // 已选择的药物数量
        allCounts: selectCount,
      }
      this.enableRange = false
    } else {
      this.rangeSelector = {
        ...this.rangeSelector,
        // 页面上药物数量，比如检索出10000条，那这里就是10000
        allCounts: total,
        maxInputLimit: 100000,
      }
      this.enableRange = true
    }

    this.init()
    // 只有在有searchParams.query时才启用自动更新相关的异步初始化
    // 药物详情页没有searchParams.query，不会执行这段逻辑，避免状态管理问题
    if (hasSearchParams) {
      setTimeout(() => {
        this.identifyAutoUpdateStatus()
      }, 1000)
    }
  }

  async getWorkspaceSearchId(params: IWorkspaceOriginParams) {
    const id = await getWorkspaceSearchId(assign({}, this.searchParams, { offset: params.from - 1, limit: params.to - params.from + 1 }))
    this.workspaceSearchId = id
  }

  toggleAutoUpdate(enable: boolean) {
    // 只有在启用了自动更新功能时才执行
    if (this.autoCollectConfig && this.autoCollectConfig.drug && this.autoUpdate) {
      this.autoCollectConfig.drug.collect_on_off = enable
      this.autoUpdate.enable = enable
    }
  }

  // 识别自动收录状态
  identifyAutoUpdateStatus() {
    try {
      // 只有在启用了自动更新功能时才执行
      if (this.autoUpdate && this.autoCollectConfig && this.autoCollectConfig.drug) {
        this.autoUpdate.enable = false
        this.autoCollectConfig.drug.collect_on_off = false
      }
    } catch (error) {
      console.error('SaveDrugService: Error in identifyAutoUpdateStatus', error)
    }
  }

  selectFolder(folder: any) {
    try {
      super.selectFolder(folder)
      this.folderSelector.selectedFolder = folder
      // 只有在有searchParams.query时才处理自动更新状态
      if (this.searchParams && this.searchParams.query) {
        this.identifyAutoUpdateStatus()
      }
    } catch (error) {
      console.error('SaveDrugService: Error in selectFolder', error)
      // 即使出错也要确保基本功能正常
      this.folderSelector.selectedFolder = folder
    }
  }

  // 提交保存到工作空间接口
  async generateSubmitSaveToWorkspaceParams(params: IWorkspaceOriginParams) {
    try {
      // 范围选择，需要先换取workspace查询id
      if (!this.selectedIds.length && !this.workspaceSearchId) {
        await this.getWorkspaceSearchId(params)
      }
      sharedCtx.service.tracking.trackGeneral({
        event_code: 'SAVE_TO_WORKSPACE',
      })
      const newParams = {
        ...params,
      }
      // 只有在启用了自动更新功能且用户选择了自动更新时才添加配置
      if (this.autoUpdate && this.autoUpdate.enable && this.autoCollectConfig) {
        newParams.auto_collect_config = this.autoCollectConfig
      }
      return formatSubmitSaveToWorkspaceParams(newParams, this.type, this.workspaceSearchId, this.selectedIds)
    } catch (error) {
      console.error('SaveDrugService: Error in generateSubmitSaveToWorkspaceParams', error)
      throw error
    }
  }

  // 出错提示回调
  onToastError({ message }: { message: string }) {
    Toast({ message, type: 'error' })
  }

  // 弹窗关闭事件（只是执行回调事件，不会关闭弹窗）
  onClose(success?: { template: string }) {
    // 触发时机：
    // 1. 用户提交，并且接口正常完成以后。
    // 2. 用户点击弹窗右上角叉叉时。
    // 3. 用户点击弹窗右下角取消按钮时。
    if (success) {
      const { template } = success
      Toast.closeAll()
      Toast({
        type: 'success',
        message: template,
        dangerouslyUseHTMLString: true,
        duration: 5000,
        showClose: true,
      })
    }
    // 确保弹窗关闭时清理任何可能存在的loading状态
    this.vm?.$emit('close')
  }
}
